// =============================================================================
// ImportAccessPanel.jsx
//
// Settings tab: whitelist of who can run the Bulk Import and see the
// (IsRestricted) activities it creates. Same CRUD shape as the Owners lookup
// in TrackerSettings.jsx, but talks to ActivityImportController instead.
//
// Instead of typing Name/User ID by hand, the admin picks a Team and then one
// of that Team's existing Activity Owners (TrackerOwner already has Name +
// UserId set up) — Name/UserId are copied from there, so there's no risk of
// typos breaking the login-identifier match.
// =============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Switch, IconButton, Tooltip, Button, CircularProgress, Alert, Chip,
  MenuItem, Select, InputLabel, FormControl, TextField, Typography,
} from '@mui/material';
import AddIcon          from '@mui/icons-material/Add';
import EditIcon          from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon          from '@mui/icons-material/Save';
import CancelIcon        from '@mui/icons-material/Cancel';
import {
  apiGetImportAuthorizedUsers,
  apiSaveImportAuthorizedUser,
  apiDeleteImportAuthorizedUser,
} from '../requests/importRequests.js';
import { apiGetTeams } from '../requests/trackerRequests.js';
import AxiosRequest from '../../../utils/AxiosRequest.js';
import config       from '../../../configuration/configuration.js';

// ActivityTracker/GetOwnersByTeam (used elsewhere for the plain Activity Owner
// picker) returns OwnerDto — Id/TeamId/Name only, no UserId. Here we need the
// UserId (to match against the login identity), which only the Settings lookup
// (ActivityTrackerSettings/Owners -> TrackerOwnerLookupDto) exposes. That
// endpoint returns every Owner across all Teams, so we filter by team client-side.
const apiGetOwnersLookup = () =>
  AxiosRequest({ method: 'GET', url: `${config.API_URL}ActivityTrackerSettings/Owners` });

const cellSx = { fontSize: '0.78rem', py: 0.75 };
const headSx = {
  fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: 0.8,
  color: 'rgba(255,255,255,0.85)', bgcolor: 'primary.main', py: 1, borderBottom: 'none',
};

const emptyRow = () => ({ id: null, teamId: '', userId: '', name: '', isActive: true, sortOrder: 0 });

const ImportAccessPanel = () => {
  const [rows, setRows]       = useState([]);
  const [teams, setTeams]     = useState([]);
  const [allOwners, setAllOwners] = useState([]);
  const [loadingOwners, setLoadingOwners] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);
  const [editId, setEditId]   = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [teamFilter, setTeamFilter] = useState('');  // '' = all teams

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    apiGetImportAuthorizedUsers()
      .then((res) => setRows(res.data ?? []))
      .catch(() => setError('Failed to load authorized users.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { apiGetTeams().then((res) => setTeams(res.data ?? [])).catch(() => {}); }, []);

  // Lista completa de Owners (todas as Teams) vem de uma vez só — é a única
  // que traz o UserId (ver nota acima do apiGetOwnersLookup).
  useEffect(() => {
    setLoadingOwners(true);
    apiGetOwnersLookup()
      .then((res) => setAllOwners(res.data ?? []))
      .catch(() => setAllOwners([]))
      .finally(() => setLoadingOwners(false));
  }, []);

  // Owners da Team escolhida, já filtrados para quem tem UserId ligado — sem
  // isso não há como validar identidade contra o login.
  const linkableOwners = allOwners.filter(
    (o) => o.isActive && !!o.userId && String(o.teamId) === String(editForm?.teamId)
  );

  // Linhas visíveis após o filtro "Filter by Team" — mesmo padrão do tab
  // Activity Owner em TrackerSettings.jsx.
  const filteredRows = teamFilter
    ? rows.filter((r) => String(r.teamId) === teamFilter)
    : rows;

  const startEdit = (row) => {
    setEditId(row.id ?? 'new');
    // teamId normalizado para string — o Select do PersonPicker compara os
    // valores dos MenuItem (String(t.id)) contra editForm.teamId.
    setEditForm({ teamId: '', ...row, teamId: row.teamId != null ? String(row.teamId) : '' });
  };
  const cancelEdit = () => { setEditId(null); setEditForm(null); };

  const handleTeamChange = (e) => {
    setEditForm((p) => ({ ...p, teamId: e.target.value, ownerRefId: '', userId: '', name: '' }));
  };

  const handleOwnerChange = (e) => {
    const ownerId = e.target.value;
    const owner = linkableOwners.find((o) => String(o.id) === String(ownerId));
    setEditForm((p) => ({ ...p, ownerRefId: ownerId, userId: owner?.userId ?? '', name: owner?.name ?? '' }));
  };

  const handleSave = () => {
    if (!editForm.userId?.trim() || !editForm.name?.trim()) return;
    setSaving(true);
    setError(null);
    apiSaveImportAuthorizedUser({ ...editForm, id: editId === 'new' ? null : editId })
      .then(() => { cancelEdit(); load(); })
      .catch(() => setError('Failed to save.'))
      .finally(() => setSaving(false));
  };

  const handleDelete = (id) => {
    if (!window.confirm('Remove this person from the Bulk Import authorized list?')) return;
    setSaving(true);
    apiDeleteImportAuthorizedUser(id)
      .then(() => load())
      .catch(() => setError('Failed to remove.'))
      .finally(() => setSaving(false));
  };

  // ── Picker usado tanto na linha "new" como numa linha em edição ───────────
  const PersonPicker = () => (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel>Team</InputLabel>
        <Select label="Team" value={editForm.teamId} onChange={handleTeamChange}>
          {teams.map((t) => <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>)}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 200 }} disabled={!editForm.teamId || loadingOwners}>
        <InputLabel>Person</InputLabel>
        <Select label="Person" value={editForm.ownerRefId ?? ''} onChange={handleOwnerChange}>
          {linkableOwners.length === 0 && (
            <MenuItem disabled value="">
              <em>
                {!editForm.teamId ? 'Select a team first'
                  : loadingOwners ? 'Loading…'
                  : 'No linked owners in this team'}
              </em>
            </MenuItem>
          )}
          {linkableOwners.map((o) => (
            <MenuItem key={o.id} value={String(o.id)}>{o.name} ({o.userId})</MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 1.5, fontSize: '0.78rem' }}>
        Only people listed here (plus Admins) can run the Bulk Import and see the activities it creates.
        Pick the Team, then the person — Name and User ID are copied from that person's Activity Owner record.
      </Alert>
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

      {/* ── Team filter — mesmo padrão do tab Activity Owner ── */}
      <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField
          select size="small" label="Filter by Team"
          value={teamFilter}
          onChange={(e) => { setTeamFilter(e.target.value); cancelEdit(); }}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value=""><em>All Teams</em></MenuItem>
          {teams.map((t) => (
            <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>
          ))}
        </TextField>
        {teamFilter && (
          <Typography variant="caption" color="text.secondary">
            {filteredRows.length} {filteredRows.length === 1 ? 'person' : 'people'}
          </Typography>
        )}
      </Box>

      <TableContainer sx={{ maxHeight: 420, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={headSx}>Team</TableCell>
              <TableCell sx={headSx}>Name</TableCell>
              <TableCell sx={headSx}>User ID</TableCell>
              <TableCell sx={{ ...headSx, width: 80 }}>Active</TableCell>
              <TableCell sx={{ ...headSx, width: 90 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3 }}><CircularProgress size={22} /></TableCell></TableRow>
            ) : filteredRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                  {teamFilter ? 'No one authorized for this team.' : 'No one authorized yet.'}
                </TableCell>
              </TableRow>
            ) : filteredRows.map((row) => {
              const isEditing = editId === row.id;
              return (
                <TableRow key={row.id} sx={{ opacity: row.isActive ? 1 : 0.45 }}>
                  {isEditing ? (
                    <TableCell sx={cellSx} colSpan={3}><PersonPicker /></TableCell>
                  ) : (
                    <>
                      <TableCell sx={cellSx}>
                        {row.teamName ?? teams.find((t) => String(t.id) === String(row.teamId))?.name ?? '—'}
                      </TableCell>
                      <TableCell sx={cellSx}>{row.name}</TableCell>
                      <TableCell sx={cellSx}>{row.userId}</TableCell>
                    </>
                  )}
                  <TableCell sx={cellSx}>
                    {isEditing ? (
                      <Switch size="small" checked={editForm.isActive}
                        onChange={(e) => setEditForm((p) => ({ ...p, isActive: e.target.checked }))} />
                    ) : (
                      <Chip label={row.isActive ? 'Active' : 'Inactive'} size="small"
                        color={row.isActive ? 'success' : 'default'} sx={{ height: 18, fontSize: '0.65rem' }} />
                    )}
                  </TableCell>
                  <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap' }}>
                    {isEditing ? (
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Save">
                          <IconButton size="small" color="primary" onClick={handleSave} disabled={saving}><SaveIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton size="small" onClick={cancelEdit} disabled={saving}><CancelIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => startEdit(row)} disabled={saving || editId !== null}><EditIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Remove">
                          <IconButton size="small" color="error" onClick={() => handleDelete(row.id)} disabled={saving || editId !== null}><DeleteOutlineIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}

            {editId === 'new' && (
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={cellSx} colSpan={3}><PersonPicker /></TableCell>
                <TableCell sx={cellSx}>
                  <Switch size="small" checked={editForm.isActive}
                    onChange={(e) => setEditForm((p) => ({ ...p, isActive: e.target.checked }))} />
                </TableCell>
                <TableCell sx={cellSx}>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Save">
                      <IconButton size="small" color="primary" onClick={handleSave} disabled={saving}><SaveIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Cancel">
                      <IconButton size="small" onClick={cancelEdit} disabled={saving}><CancelIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 1.5 }}>
        <Button variant="outlined" size="small" startIcon={<AddIcon />}
          onClick={() => startEdit(emptyRow())} disabled={editId !== null || loading}>
          Add Authorized User
        </Button>
      </Box>
    </Box>
  );
};

export default ImportAccessPanel;
